import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FiClock, FiTrendingUp } from 'react-icons/fi';

interface HeroProps {
  featuredMovie?: {
    title: string;
    releaseDate: string;
    backdrop: string;
  };
}

const Hero: React.FC<HeroProps> = ({ featuredMovie }) => {
  const { user: isSignedIn } = useAuth();
  const isAuthenticated = !!isSignedIn;
  const defaultBackdrop = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1925&q=80';

  const scrollToUpcoming = () => {
    const element = document.getElementById('upcoming-movies');
    if (element) {
      const navbarHeight = 70; // Approximate height of the fixed navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative h-[50vh] md:h-[70vh] min-h-[300px] md:min-h-[380px] flex items-center pt-16 pb-16">
      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={featuredMovie?.backdrop || defaultBackdrop}
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-dark/90 via-background-dark/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="container-custom relative z-20 px-4 md:px-6 pt-16 md:pt-0">
        <div className="max-w-2xl animate-fade-in ml-0 md:ml-4">
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white/80 text-shadow-sm mb-3">
            Your Ultimate Movie Watchlist Awaits!
          </h1>
          
          <p className="text-sm md:text-lg text-white/70 mb-5 md:mb-6">
            Discover upcoming releases, track your favorites with our new watchlist feature, and stay ahead of the cinematic curve!
          </p>

          <div className="flex flex-col sm:flex-row gap-2 md:gap-4 mb-5 md:mb-6">
            <div className="flex items-center space-x-2 text-white/70">
              <FiClock className="w-3.5 md:w-4.5 h-3.5 md:h-4.5 text-accent-teal" />
              <span className="text-xs md:text-sm">Updated daily</span>
            </div>
            <div className="flex items-center space-x-2 text-white/70">
              <FiTrendingUp className="w-3.5 md:w-4.5 h-3.5 md:h-4.5 text-accent-orange" />
              <span className="text-xs md:text-sm">Real-time hype scores</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={scrollToUpcoming}
              className="btn-primary w-full sm:w-auto text-sm"
            >
              Explore Upcoming Movies
            </button>
            {isAuthenticated && (
              <Link
                to="/watchlist"
                className="btn-secondary w-full sm:w-auto text-sm"
              >
                My Watchlist
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-14 md:h-18 bg-gradient-to-t from-background-light to-transparent z-10" />
    </div>
  );
};

export default Hero;